/**
 * Single source of truth for marketing pricing.
 * Tab order on the home page matches Object.keys order: profile, partners, skills, pilot, cv.
 * Each module's middle tier carries `popular: true`.
 *
 * Profile pricing comes from feedback item 3 (10 RFPs / 20 RFPs / Custom).
 * Partners / Skills / Pilot / CV pricing is ported verbatim from the previous
 * Home.jsx pricing block (NOT the design reference; the reference uses sample data).
 */

export const PRICING = {
  profile: {
    label: "ASAP Profile",
    tiers: [
      {
        key: "10rfp",
        name: "10 RFPs",
        price: "170€",
        suffix: "/mois",
        description: "Idéal pour démarrer le sourcing automatisé sur vos appels d'offres.",
        features: [
          "10 appels d'offres traités par mois",
          "Sourcing automatisé multi-sources",
          "Scoring des candidats",
          "Export shortlist enrichie",
        ],
      },
      {
        key: "20rfp",
        name: "20 RFPs",
        price: "300€",
        suffix: "/mois",
        description: "Pour les équipes qui répondent à plus d'opportunités chaque mois.",
        features: [
          "20 appels d'offres traités par mois",
          "Sourcing automatisé multi-sources",
          "Scoring des candidats",
          "Export shortlist enrichie",
          "Support prioritaire",
        ],
        popular: true,
      },
      {
        key: "custom",
        name: "Custom",
        price: "Sur demande",
        description: "Volume sur mesure pour les ESN à grande échelle.",
        features: [
          "Volume RFP personnalisé",
          "Intégrations sur mesure",
          "Accompagnement dédié",
          "SLA personnalisé",
        ],
      },
    ],
  },

  partners: {
    label: "ASAP Partners",
    tiers: [
      {
        key: "core",
        name: "Core",
        price: "89€",
        suffix: "/mois",
        priceNote: "par Business Unit",
        description: "Accélérez l'inter-contrat avec les fonctionnalités essentielles.",
        features: [
          "Diffusion par mails des appels d'offres",
          "Création lien formulaire",
          "Filtres des CV reçus",
          "Mail de refus",
          "Intégration du CV dans CRM",
        ],
      },
      {
        key: "ultimate",
        name: "Ultimate",
        price: "129€",
        suffix: "/mois",
        priceNote: "par Business Unit",
        description: "Tout ce qu'il y a dans Core, plus l'automatisation avancée.",
        features: [
          "Tout ce qu'il y a dans Core",
          "Automatisation diffusion de vos profils en intercontrat",
          "Filtrage avancé",
          "Calcul des marges automatisé",
        ],
        popular: true,
      },
      {
        key: "custom",
        name: "Custom",
        price: "Sur devis",
        description: "Pour un abonnement sur mesure adapté à votre réseau de partenaires.",
        features: [
          "Tout ce qu'il y a dans Ultimate",
          "Intégrations et workflows sur mesure",
          "Accompagnement dédié",
        ],
      },
    ],
  },

  skills: {
    label: "ASAP Skills",
    tiers: [
      {
        key: "starter",
        name: "Starter",
        price: "100€",
        suffix: "/mois",
        priceNote: "pour 10 tests (10€/test)",
        description: "Idéal pour démarrer. Validez l'expertise technique de vos candidats.",
        features: [
          "Nb illimité d'utilisateurs",
          "IDE intégré",
          "Bases de questions illimitées",
          "Rapports détaillés par candidat",
          "Technologie anti-triche",
        ],
      },
      {
        key: "team",
        name: "Team",
        price: "255€",
        suffix: "/mois",
        priceNote: "pour 30 tests (8,5€/test)",
        description: "Pour plus de ressources. Validez l'expertise technique à grande échelle.",
        features: [
          "Nb illimité d'utilisateurs",
          "IDE intégré",
          "Bases de questions illimitées",
          "Rapports détaillés par candidat",
          "Technologie anti-triche",
        ],
        popular: true,
      },
      {
        key: "custom",
        name: "Custom",
        price: "Sur devis",
        description: "Facturation annuelle. Pour l'abonnement le plus adapté.",
        features: [
          "Nb illimité d'utilisateurs",
          "IDE intégré",
          "Bases de questions illimitées",
          "Rapports détaillés par candidat",
          "Technologie anti-triche",
        ],
      },
    ],
  },

  pilot: {
    label: "ASAP Pilot",
    tiers: [
      {
        key: "base",
        name: "Base",
        price: "59€",
        suffix: "/manager/mois",
        priceNote: "+ 4€/consultant/mois",
        description: "Centralisez toutes vos données pour piloter votre activité en un seul endroit.",
        features: [
          "CRM, Gestion des temps, Suivi RH",
          "Scoring ATS (appels d'offres et CV)",
          "Matching profils/besoins",
          "Mesure de la performance, KPI",
          "Intégration du CV dans CRM",
        ],
      },
      {
        key: "growth",
        name: "Growth",
        price: "Sur devis",
        description: "Scale-up : ajoutez l'automatisation et l'évaluation dans Pilot.",
        features: [
          "Tout ce qu'il y a dans Base",
          "Évaluation des compétences automatisée",
          "Workflows commerciaux personnalisés",
          "Tableaux de bord avancés",
        ],
        popular: true,
      },
      {
        key: "custom",
        name: "Custom",
        price: "Sur devis",
        description: "Pour un abonnement sur mesure adapté à votre organisation.",
        features: [
          "CRM, Gestion des temps, Suivi RH",
          "Scoring ATS (appels d'offres et CV)",
          "Matching profils/besoins",
          "Intégrations sur mesure",
          "Accompagnement dédié",
        ],
      },
    ],
  },

  cv: {
    label: "ASAP CV",
    tiers: [
      {
        key: "starter",
        name: "Starter",
        price: "15€",
        suffix: "/mois",
        priceNote: "10 CV/mois",
        description: "Idéal pour démarrer la génération de dossiers de compétences.",
        features: [
          "10 CV par mois",
          "CV original",
          "CV retouché IA",
          "Scoring avec appel d'offre (ATS)",
          "Guide oral",
        ],
      },
      {
        key: "elite",
        name: "Elite",
        price: "45€",
        suffix: "/mois",
        priceNote: "40 CV/mois",
        description: "Pour produire plus de ressources tout en gardant un rendu homogène.",
        features: [
          "40 CV par mois",
          "CV original",
          "CV retouché IA",
          "Scoring avec appel d'offre (ATS)",
          "Guide oral",
          "Questions entretiens",
        ],
        popular: true,
      },
      {
        key: "custom",
        name: "Custom",
        price: "Sur devis",
        description: "Le tout-en-un pour les grandes équipes.",
        features: [
          "Volume CV personnalisé",
          "CV original",
          "CV retouché IA",
          "Scoring avec appel d'offre (ATS)",
          "Guide oral",
          "Questions entretiens",
        ],
      },
    ],
  },
};

export const MODULE_ORDER = ["profile", "partners", "skills", "pilot", "cv"];
